import { Check, CheckHighlight } from "@monta/icons";
import clsx from "clsx";
import type { CSSProperties } from "react";
import React, { useEffect, useMemo, useState } from "react";
import { getColor } from "../../../scss";
import { useHeightObserver, useResize } from "../../../utils";
import { cssUnit } from "../../types";
import styles from "./Wizard.module.scss";

export interface WizardItemProps {
  children: React.ReactNode | React.ReactNode[];
  className?: string;
}

export type TWizardItemRef = {
  title: string;
} & React.RefObject<HTMLDivElement>;

export const WizardItem = React.forwardRef<TWizardItemRef, WizardItemProps>(
  (props: WizardItemProps, ref) => {
    const { children, className } = props;
    const stepRef = React.useRef<HTMLDivElement>(null);

    React.useImperativeHandle(
      ref,
      () => ({
        title: "test",
        ...stepRef,
      }),
      [],
    );
    return (
      <div ref={stepRef} className={clsx(className, styles["wizard-item"])}>
        {children}
      </div>
    );
  },
);

type TWizardChild = React.ReactElement<
  WizardItemProps & { ref: React.ForwardedRef<TWizardItemRef> }
>;

export interface WizardProps {
  children: TWizardChild | TWizardChild[];
  className?: string;
  stepsDisabled?: boolean;
  onActiveStepChange?: (activeStep: number) => void;
}

export type TWizardRef = {
  next: () => void;
  previous: () => void;
  goto: (step: number) => void;
  activeStep: number;
};

const Wizard = React.forwardRef<TWizardRef, WizardProps>((props, ref) => {
  const { children, className, onActiveStepChange } = props;
  const stepRefs = React.useRef<Record<number, TWizardItemRef>>({});
  const sliderRef = React.useRef<HTMLDivElement>(null);
  const wizardRef = React.useRef<HTMLDivElement>(null);
  const [activeStep, setActiveStep] = React.useState(0);

  const scrollToStep = () => {
    const el = sliderRef.current;
    if (!el) return;
    el.scrollTo({
      left: -(activeStep * sliderRef.current.clientWidth),
      behavior: "smooth",
    });
  };

  useResize(scrollToStep, false, 300, [activeStep]);
  useEffect(() => {
    onActiveStepChange?.(activeStep);
    scrollToStep();
  }, [activeStep]);

  useEffect(() => {
    if (location.hash.includes("wizard-step")) {
      const index =
        location.hash.lastIndexOf("wizard-step=") + "wizard-step=".length;
      const slice = location.hash.slice(index);
      const hashStep = Number(slice) - 1;
      if (!isNaN(hashStep)) setActiveStep(hashStep);
    }
  }, []);

  const items = useMemo(
    () => (Array.isArray(children) ? children : [children]),
    [children],
  );

  const wizardSteps = items.map((item, index) =>
    React.cloneElement(item, {
      ...item.props,
      ref: (el) => Object.assign(stepRefs.current, { [index]: el }),
    }),
  );

  React.useImperativeHandle(
    ref,
    () => ({
      next() {
        if (activeStep < items.length - 1) {
          setActiveStep((i) => i + 1);
        }
      },
      previous() {
        if (activeStep >= 1) {
          setActiveStep((i) => i - 1);
        }
      },
      goto(step: number) {
        setActiveStep(step);
      },
      activeStep,
    }),
    [activeStep, items],
  );

  const steps = useMemo(() => {
    return [...new Array(items.length).keys()].map((i) => (
      <>
        {i != 0 && <hr />}
        <div
          key={`wizard-step-${i}`}
          onClick={() => {
            // TODO: Handle correctly
            // if (!stepsDisabled) setActiveStep(i);
          }}
          className={clsx(
            styles["wizard__steps__step"],
            i == activeStep && styles["wizard__steps__step--active"],
            i < activeStep && styles["wizard__steps__step--done"],
          )}
        >
          {i < activeStep ? (
            <CheckHighlight style={{ color: "#FF6A00" }} />
          ) : i == activeStep ? (
            <Check style={{ color: "#FF6A00" }} />
          ) : (
            <Check color={getColor("neutral")} />
          )}
        </div>
      </>
    ));
  }, [items, activeStep]);

  const [currentSlideRef, setCurrentSlideRef] = useState(
    stepRefs.current[activeStep],
  );
  const sliderHeight = useHeightObserver(currentSlideRef, [currentSlideRef]);
  useEffect(() => {
    setCurrentSlideRef(stepRefs.current[String(activeStep)]);
  }, [activeStep, steps]);

  return (
    <div className={clsx(styles["wizard"], className)} ref={wizardRef}>
      <div className={styles["wizard__steps"]}>{steps}</div>
      <div
        className={styles["wizard__slider"]}
        ref={sliderRef}
        style={
          {
            maxHeight: sliderHeight && cssUnit(sliderHeight),
            height: sliderHeight && cssUnit(sliderHeight),
            minHeight: sliderHeight && cssUnit(sliderHeight),
          } as CSSProperties
        }
      >
        {wizardSteps}
      </div>
    </div>
  );
});

export default Wizard;
